import { useState } from 'react';
import { Users, Plus, Bell, Settings, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PostList from '@/components/community/PostList';
import PostForm from '@/components/community/PostForm';
import NotificationBell from '@/components/community/NotificationBell';
import CommunitiesList from '@/components/community/CommunitiesList';
import { postsAPI } from '@/api/community';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { getUserData } from '@/utils/auth';
import { useNavigate } from 'react-router-dom';

export default function AdminCommunityPage() {
  const [activeTab, setActiveTab] = useState('announcements');
  const [showPostForm, setShowPostForm] = useState(false);
  const [showCommunities, setShowCommunities] = useState(false);
  const [showSavedPosts, setShowSavedPosts] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const userData = getUserData('admin');
  const userType = userData?.user_type || 'admin';
  const navigate = useNavigate();

  const handlePostCreated = () => {
    setRefreshKey(prev => prev + 1);
    setShowPostForm(false);
    try { trackEvent('admin_post_created', attachUserProps({ page: 'admin_community' })); } catch (e) {}
  };

  const handlePostUpdate = () => {
    setRefreshKey(prev => prev + 1);
    try { trackEvent('admin_post_updated', attachUserProps({ page: 'admin_community' })); } catch (e) {}
  };

  // Fetch functions for each tab
  const fetchAnnouncements = (page, pageSize) => {
    return postsAPI.getAnnouncements(page, pageSize);
  };

  const fetchMyFeed = (page, pageSize) => {
    return postsAPI.getMyFeed(page, pageSize);
  };

  const fetchAllPosts = (page, pageSize) => {
    return postsAPI.getAllPosts(page, pageSize);
  };

  const fetchSavedPosts = (page, pageSize) => {
    return postsAPI.getSavedPosts(page, pageSize);
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-3 sm:p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto w-full space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-3 sm:gap-4">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-[hsl(var(--green-bright))]" />
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">Community</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Manage and engage with your school community</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <NotificationBell />
            <Button
              variant="outline"
              onClick={() => { setShowCommunities(true); try { trackEvent('admin_open_manage_communities', attachUserProps({ page: 'admin_community' })); } catch (e) {} }}
              className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] text-xs sm:text-sm px-3 py-2"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Communities
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowSavedPosts(true); try { trackEvent('admin_open_saved_posts', attachUserProps({ page: 'admin_community' })); } catch (e) {} }}
              className="text-[hsl(var(--green-bright))] hover:text-[hsl(var(--teal-vivid))] text-xs sm:text-sm px-3 py-2"
            >
              <Bookmark className="w-4 h-4 mr-2" />
              Saved Posts
            </Button>
            <Button
              onClick={() => { setShowPostForm(true); try { trackEvent('admin_open_new_post', attachUserProps({ page: 'admin_community' })); } catch (e) {} }}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-xs sm:text-sm px-3 py-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Three Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="announcements" className="text-xs sm:text-sm">Announcements</TabsTrigger>
            <TabsTrigger value="my-feed" className="text-xs sm:text-sm">My Feed</TabsTrigger>
            <TabsTrigger value="posts" className="text-xs sm:text-sm">Posts</TabsTrigger>
          </TabsList>

          <TabsContent value="announcements" className="mt-4">
            <PostList
              key={`announcements-${refreshKey}`}
              fetchFunction={fetchAnnouncements}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>

          <TabsContent value="my-feed" className="mt-4">
            <PostList
              key={`my-feed-${refreshKey}`}
              fetchFunction={fetchMyFeed}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <PostList
              key={`posts-${refreshKey}`}
              fetchFunction={fetchAllPosts}
              onPostUpdate={handlePostUpdate}
              isGuardian={false}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Post Form Modal */}
      <PostForm
        isOpen={showPostForm}
        onClose={() => setShowPostForm(false)}
        onPostCreated={handlePostCreated}
        userType={userType}
      />

      {/* Communities Management Modal */}
      {showCommunities && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCommunities(false)}>
          <div className="bg-[hsl(var(--card-bg))] rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-base sm:text-2xl font-bold text-foreground">Manage Communities</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCommunities(false)}
                className="text-xs sm:text-base"
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              <CommunitiesList
                isOpen={showCommunities}
                onClose={() => setShowCommunities(false)}
                isAdmin={true}
                isTeacher={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Saved Posts Modal */}
      {showSavedPosts && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSavedPosts(false)}>
          <div className="bg-[hsl(var(--card-bg))] rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 sm:p-6 border-b border-[hsl(var(--card-border))]">
              <h2 className="text-base sm:text-2xl font-bold text-foreground">Saved Posts</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSavedPosts(false)}
                className="text-xs sm:text-base"
              >
                ×
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              <PostList
                key={`saved-posts-${refreshKey}`}
                fetchFunction={fetchSavedPosts}
                onPostUpdate={handlePostUpdate}
                isGuardian={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
